/**
 * The physics behind the now-playing sheet's swipe-to-dismiss.
 *
 * Two behaviours separate a sheet that feels native from one that feels like a div with a
 * transform on it:
 *
 *   1. **Rubber banding.** Dragging the wrong way (upward, when the sheet can only leave
 *      downward) must not be ignored — that reads as a dropped touch — and must not track
 *      the finger either. It resists, moving a shrinking fraction of the distance, which is
 *      the platform's way of saying "this is as far as it goes".
 *
 *   2. **Velocity.** A short, fast flick should dismiss; a long, slow drag that stops
 *      halfway should snap back. Distance alone gets both wrong. So the decision looks at
 *      distance *or* the speed at release, in the spirit of iOS's own sheet dismissal.
 *
 * Pure functions with no DOM and no timers, so the whole thing is testable and the component
 * keeps only the pointer plumbing.
 */

/** Past this many pixels of travel the sheet dismisses regardless of speed. */
export const DISMISS_DISTANCE_PX = 120;

/** Below this speed (px/ms) a release is a settle, not a flick. */
export const DISMISS_VELOCITY_PX_PER_MS = 0.5;

/** A flick this fast dismisses from as little as a quarter of the distance threshold. */
export const FLICK_DISTANCE_PX = DISMISS_DISTANCE_PX / 4;

/**
 * Map raw finger travel onto sheet travel.
 *
 * Downward is free movement. Upward is resisted on an asymptotic curve, so the sheet keeps
 * responding but never rises far, no matter how hard it is pulled.
 */
export function rubberBand(delta: number, limit = 120): number {
  if (delta >= 0) return delta;
  const over = -delta;
  return -(over * limit) / (over + limit);
}

export interface Sample {
  y: number;
  t: number;
}

/**
 * Shortest window a velocity estimate is trusted over.
 *
 * Two pointer events a millisecond apart put 40px of travel at 40px/ms — eighty times the
 * flick threshold — from what was actually a slow drag that happened to deliver two samples
 * back to back. A real flick spans several frames, so anything shorter than this is treated
 * as no information rather than as enormous speed, and the distance rule decides alone.
 */
export const MIN_VELOCITY_WINDOW_MS = 10;

/**
 * How far back a flick is measured over.
 *
 * Long enough to span several frames at typical pointer rates, so the estimate reflects the
 * gesture rather than one jittery sample pair.
 */
export const VELOCITY_WINDOW_MS = 30;

/**
 * Speed at release, in px/ms, from the recent pointer samples.
 *
 * Only the last few samples count: a drag that paused for a second and then moved a little
 * is slow, no matter how far the finger travelled overall.
 */
export function velocityFrom(samples: readonly Sample[]): number {
  if (samples.length < 2) return 0;
  const last = samples[samples.length - 1];
  // Prefer the first sample at least `VELOCITY_WINDOW_MS` back, so the estimate spans several
  // frames rather than one pair. When no sample is that old — a gesture that has only just
  // begun — this falls through to `samples[0]` and the guard below rejects the result.
  let first = samples[0];
  for (let i = samples.length - 2; i >= 0; i--) {
    first = samples[i];
    if (last.t - samples[i].t >= VELOCITY_WINDOW_MS) break;
  }
  const dt = last.t - first.t;
  if (dt < MIN_VELOCITY_WINDOW_MS) return 0;
  return (last.y - first.y) / dt;
}

/** Should this release dismiss the sheet? */
export function shouldDismiss(offset: number, velocity: number): boolean {
  if (offset >= DISMISS_DISTANCE_PX) return true;
  return velocity >= DISMISS_VELOCITY_PX_PER_MS && offset >= FLICK_DISTANCE_PX;
}

/** Keep the sample buffer short — anything older than this is noise for a velocity estimate. */
export const MAX_SAMPLES = 6;

export function pushSample(samples: Sample[], sample: Sample): Sample[] {
  samples.push(sample);
  if (samples.length > MAX_SAMPLES) samples.shift();
  return samples;
}
